import axios from "axios";
import { DataGrid, DropDownBox } from "devextreme-react";
import {
  Column,
  Editing,
  FilterRow,
  Form,
  LoadPanel,
  Paging,
  Popup,
  Scrolling,
  SearchPanel,
  Selection,
  StateStoring,
  Texts,
  Toolbar,
} from "devextreme-react/data-grid";
import {
  SimpleItem,
  Label,
  RequiredRule,
  EmptyItem,
} from "devextreme-react/form";
import { Item } from "devextreme-react/toolbar";
import CustomStore from "devextreme/data/custom_store";
import React, { useRef } from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getDateString } from "../common/date";
import { degree } from "./data";
import UniversityDropDownBox from "./UniversityDropDownBox";

export default function Education() {
  const [dataSource, setDataSource] = useState(null);
  const [present, setPresent] = useState(null);

  const userInfo = useSelector((state) => state.userInfo);

  const universityDropDownBoxRef = useRef(null);
  const educationGridRef = useRef(null);

  // let univId = null;

  useEffect(async () => {
    setDataSource(
      new CustomStore({
        key: "SEQ",
        load: async () => {
          const result = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/education/${userInfo.employeeNo}`
          );

          return result.data;
        },
        insert: async (values) => {
          await axios.post(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/education/${userInfo.employeeNo}`,
            {
              ...values,
              univId: universityDropDownBoxRef.current.instance.option("value"),
            }
          );
        },
        update: async (key, values) => {
          await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/education/${userInfo.employeeNo}/${key}`,
            {
              ...values,
              univId: universityDropDownBoxRef.current.instance.option("value"),
            }
          );
        },
        remove: async (key) => {
          await axios.delete(
            `${process.env.REACT_APP_BECCO_API_URL}/api/cv/education/${userInfo.employeeNo}/${key}`
          );
        },
      })
    );
  }, []);

  const calculateCellValue = (data) => {
    return `${getDateString(new Date(data.startDate))}~${
      data.ps == 1 ? "Present" : getDateString(new Date(data.endDate))
    }`;
  };

  const calculateUniversityCellValue = (data) => {
    return `${data.schoolName}, ${data.maj} (${getDegree(data.degree)})`;
  };

  const getDegree = (_degree) => {
    for (let i = 0; i < degree.length; i++) {
      if (degree[i].id == _degree) {
        return degree[i].name;
      }
    }
  };

  const onEditingStart = (e) => {
    e.component.selectRows(e.key, true);

    // univId = Array.of(e.data.univId);
    // setUnivId(Array.of(e.data.univId));
  };

  const onHidden = (e) => {
    educationGridRef.current.instance.deselectAll();
  };

  const onSaving = React.useCallback(async (e) => {
    if (e.changes.length == 0) {
      const SEQ =
        educationGridRef.current.instance.option("editing.editRowKey");

      await axios.put(
        `${process.env.REACT_APP_BECCO_API_URL}/api/cv/education/${userInfo.employeeNo}/${SEQ}`,
        {
          univId: universityDropDownBoxRef.current.instance.option("value")[0],
        }
      );

      educationGridRef.current.instance.refresh();
    }
  });

  const setCellValue = (newData, value) => {
    newData.ps = value;
  };

  const setEndDateCellValue = (newData, value) => {
    newData.endDate = value;
  };

  const onInitNewRow = (e) => {
    e.data.ps = false;
  };

  const customizeItem = (item) => {
    if (item) {
      let gridInstance = educationGridRef.current.instance;
      let editRowKey = gridInstance.option("editing.editRowKey");
      let index = gridInstance.getRowIndexByKey(editRowKey);
      index = index === -1 ? 0 : index;

      if (item.dataField == "endDate") {
        if (gridInstance.cellValue(index, "ps") == true) {
          item.disabled = true;
        }
      } else if (item.dataField == "ps") {
        if (gridInstance.cellValue(index, "endDate")) {
          item.disabled = true;
        }
      }
    }
  };

  return (
    <DataGrid
      id="educationGrid"
      ref={educationGridRef}
      dataSource={dataSource}
      rowAlternationEnabled={true}
      showBorders={true}
      showColumnLines={true}
      allowColumnResizing={true}
      columnAutoWidth={true}
      wordWrapEnabled={true}
      allowColumnReordering={true}
      style={{
        maxHeight: "calc(100vh - 180px)",
        marginLeft: "10px",
        marginRight: "10px",
        // marginTop: "56px",
      }}
      hoverStateEnabled={true}
      showColumnHeaders={false}
      onEditingStart={onEditingStart}
      onSaving={onSaving}
      onInitNewRow={onInitNewRow}
    >
      <LoadPanel enabled={true} />

      <SearchPanel visible={true} />

      <Scrolling mode={"virtual"} showScrollbar={"always"} />

      <Selection mode={"single"} />

      {/* <StateStoring
        enabled={true}
        type="localStorage"
        storageKey={"CurriculumVitae.EducationDataGrid"}
      /> */}

      <Editing
        mode={"popup"}
        allowAdding={true}
        allowDeleting={true}
        allowUpdating={true}
      >
        <Texts
          confirmDeleteTitle={"Delete the Education"}
          confirmDeleteMessage={
            "Are you sure you want to delete this education?"
          }
        />
        <Popup
          title={"Education"}
          showTitle={true}
          height={250}
          onHidden={onHidden}
        />
        <Form
          labelMode={"outside"}
          showColonAfterLabel={false}
          labelLocation="top"
          minColWidth={300}
          colCount={2}
          colCountByScreen={{ sm: 2, md: 4 }}
          screenByWidth={(width) => {
            return width < 720 ? "sm" : "md";
          }}
          validationGroup="education"
          height={"100%"}
          customizeItem={customizeItem}
        >
          <SimpleItem
            dataField={"startDate"}
            editorType="dxDateBox"
            editorOptions={{
              type: "date",
              displayFormat: "yyyy-MM-dd",
              dateSerializationFormat: "yyyy-MM-dd",
              showClearButton: true,
              max: new Date(),
            }}
          >
            <Label text="Start Date" />
            <RequiredRule message={"* Start Date를 입력해주세요."} />
          </SimpleItem>
          <SimpleItem
            dataField={"endDate"}
            editorType="dxDateBox"
            editorOptions={{
              type: "date",
              displayFormat: "yyyy-MM-dd",
              dateSerializationFormat: "yyyy-MM-dd",
              showClearButton: true,
              max: new Date(),
              // disabled: present == true ? true : false,
            }}
          >
            <Label text={"End date"} />
          </SimpleItem>
          <SimpleItem
            dataField={"ps"}
            editorType="dxCheckBox"
            editorOptions={{
              text: "Present",
            }}
          >
            <Label visible={false} />
          </SimpleItem>
          <EmptyItem />
          <SimpleItem colSpan={2} dataField="univId">
            <Label text={"University"} />
            <UniversityDropDownBox
              universityDropDownBoxRef={universityDropDownBoxRef}
              educationGridRef={educationGridRef}
            />
            <RequiredRule message={"* University를 입력해주세요."} />
          </SimpleItem>
          <SimpleItem
            dataField={"maj"}
            editorType="dxTextBox"
            editorOptions={{
              showClearButton: true,
            }}
          >
            <RequiredRule message={"* Major를 입력해주세요."} />
          </SimpleItem>
          <SimpleItem
            dataField={"degree"}
            editorType="dxSelectBox"
            editorOptions={{
              showClearButton: true,
              items: degree,
              valueExpr: "id",
              displayExpr: "name",
            }}
          >
            <RequiredRule message={"* Degree를 입력해주세요."} />
          </SimpleItem>
        </Form>
      </Editing>

      <Column
        dataField={"startDate"}
        caption="Start Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        visible={false}
      />
      <Column
        dataField="endDate"
        caption="End Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        visible={false}
        setCellValue={setEndDateCellValue}
      />
      <Column
        dataField={"ps"}
        caption="Present"
        dataType={"string"}
        visible={false}
        setCellValue={setCellValue}
      />
      <Column
        dataField={"duration"}
        caption="Duration"
        dataType={"string"}
        calculateCellValue={calculateCellValue}
        width={200}
      />
      <Column
        dataField={"univId"}
        caption="University Id"
        dataType={"string"}
        visible={false}
      />
      <Column
        dataField={"schoolName"}
        caption="University"
        dataType={"string"}
        calculateCellValue={calculateUniversityCellValue}
      />
      <Column
        dataField={"maj"}
        caption="Major"
        dataType={"string"}
        visible={false}
      />
      <Column dataField={"degree"} caption="Degree" visible={false} />

      <Toolbar>
        <Item location={"before"}>
          <span
            style={{ fontWeight: 500, color: "#009688", marginLeft: "10px" }}
          >
            EDUCATION
          </span>
        </Item>
        <Item location="after" name="addRowButton" />
        <Item location="after" name="searchPanel" />
      </Toolbar>
    </DataGrid>
  );
}
